if (!customElements.get('brands-directory')) {
  class BrandsDirectory extends HTMLElement {
    constructor() {
      super();
      this.searchInput = this.querySelector('[data-brands-search]');
      this.clearButton = this.querySelector('[data-brands-clear]');
      this.resetButton = this.querySelector('[data-brands-reset]');
      this.status = this.querySelector('[data-brands-status]');
      this.emptyState = this.querySelector('[data-brands-empty]');
      this.tools = this.querySelector('[data-brands-tools]');
      this.alphabet = this.querySelector('[data-brands-alphabet]');
      this.alphabetShell = this.querySelector('[data-brands-alphabet-shell]');
      this.scrollButtons = Array.from(this.querySelectorAll('[data-brands-scroll]'));
      this.items = Array.from(this.querySelectorAll('[data-brand-item]'));
      this.groups = Array.from(this.querySelectorAll('[data-brand-group]'));
      this.filters = Array.from(this.querySelectorAll('[data-brand-filter]'));
      this.activeLetter = 'all';
      this.setToolsOffset = this.setToolsOffset.bind(this);
      this.updateStickyState = this.updateStickyState.bind(this);
      this.updateAlphabetOverflow = this.updateAlphabetOverflow.bind(this);
    }

    connectedCallback() {
      if (!this.searchInput || !this.items.length) return;

      this.availableLetters = new Set(this.items.map((item) => item.dataset.brandLetter));
      this.filters.forEach((filter) => {
        const letter = filter.dataset.brandFilter;
        if (letter !== 'all' && !this.availableLetters.has(letter)) filter.disabled = true;
        filter.addEventListener('click', () => this.setLetter(letter));
      });

      this.searchInput.addEventListener('input', () => {
        if (this.activeLetter !== 'all') {
          this.setLetter('all', { updateResults: false, scrollResults: false });
        }
        this.update();
        this.scrollResultsToTop('auto');
      });
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.searchInput.value) this.clearSearch();
      });
      this.clearButton?.addEventListener('click', () => this.clearSearch());
      this.resetButton?.addEventListener('click', () => this.reset());

      this.scrollButtons.forEach((button) => {
        button.addEventListener('click', () => this.scrollAlphabet(button.dataset.brandsScroll));
      });

      this.alphabet?.addEventListener('scroll', this.updateAlphabetOverflow, { passive: true });
      window.addEventListener('scroll', this.updateStickyState, { passive: true });
      window.addEventListener('resize', this.updateStickyState, { passive: true });

      if (this.tools) {
        this.toolsObserver = new ResizeObserver(this.setToolsOffset);
        this.toolsObserver.observe(this.tools);
        this.setToolsOffset();
      }
      this.stickyHeader = document.querySelector('.cc-header--sticky');
      if (this.stickyHeader) {
        this.headerObserver = new ResizeObserver(this.updateStickyState);
        this.headerObserver.observe(this.stickyHeader);
      }
      this.updateStickyState();
      this.updateAlphabetOverflow();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.resultsScrollFrame);
      this.toolsObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.alphabet?.removeEventListener('scroll', this.updateAlphabetOverflow);
      window.removeEventListener('scroll', this.updateStickyState);
      window.removeEventListener('resize', this.updateStickyState);
    }

    setToolsOffset() {
      const offset = (this.tools?.offsetHeight || 0) + 28;
      this.style.setProperty('--brands-tools-offset', `${offset}px`);
      this.updateAlphabetOverflow();
    }

    updateStickyState() {
      if (!this.tools) return;
      const isMobile = window.matchMedia('(max-width: 599px)').matches;
      if (isMobile && this.stickyHeader) {
        const visibleHeaderBottom = Math.max(0, Math.round(this.stickyHeader.getBoundingClientRect().bottom));
        this.style.setProperty('--brands-sticky-top', `${visibleHeaderBottom + 2}px`);
      } else {
        this.style.removeProperty('--brands-sticky-top');
      }
      const stickyTop = Number.parseFloat(window.getComputedStyle(this.tools).top) || 0;
      this.tools.classList.toggle('is-stuck', this.tools.getBoundingClientRect().top <= stickyTop + 1);
    }

    updateAlphabetOverflow() {
      if (!this.alphabet || !this.alphabetShell) return;
      const maxScroll = this.alphabet.scrollWidth - this.alphabet.clientWidth;
      const hasOverflow = maxScroll > 2;
      const canScrollLeft = hasOverflow && this.alphabet.scrollLeft > 2;
      const canScrollRight = hasOverflow && this.alphabet.scrollLeft < maxScroll - 2;
      this.alphabetShell.classList.toggle('has-overflow', hasOverflow);
      this.alphabetShell.classList.toggle('can-scroll-left', canScrollLeft);
      this.alphabetShell.classList.toggle('can-scroll-right', canScrollRight);
      this.scrollButtons.forEach((button) => {
        const canScroll = button.dataset.brandsScroll === 'previous' ? canScrollLeft : canScrollRight;
        button.disabled = !canScroll;
      });
    }

    scrollAlphabet(direction) {
      if (!this.alphabet) return;
      const distance = Math.max(180, this.alphabet.clientWidth * 0.7);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.alphabet.scrollBy({
        left: direction === 'previous' ? -distance : distance,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    setLetter(letter, { updateResults = true, scrollResults = true } = {}) {
      this.activeLetter = letter;
      this.filters.forEach((filter) => {
        const isActive = filter.dataset.brandFilter === letter;
        filter.classList.toggle('is-active', isActive);
        filter.setAttribute('aria-pressed', isActive.toString());
      });
      const activeFilter = this.filters.find((filter) => filter.dataset.brandFilter === letter);
      activeFilter?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      if (updateResults) this.update();
      if (scrollResults) this.scrollResultsToTop();
    }

    clearSearch() {
      this.searchInput.value = '';
      this.searchInput.focus();
      this.update();
      this.scrollResultsToTop();
    }

    reset() {
      this.searchInput.value = '';
      this.setLetter('all');
      this.searchInput.focus();
    }

    scrollResultsToTop(behavior = 'smooth') {
      if (!this.status || !this.tools) return;
      window.cancelAnimationFrame(this.resultsScrollFrame);
      this.resultsScrollFrame = window.requestAnimationFrame(() => {
        this.updateStickyState();
        const stickyTop = Number.parseFloat(window.getComputedStyle(this.tools).top) || 0;
        const targetTop = this.status.getBoundingClientRect().top + window.scrollY;
        const scrollTop = Math.max(0, targetTop - stickyTop - this.tools.offsetHeight - 12);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          top: scrollTop,
          behavior: reducedMotion ? 'auto' : behavior,
        });
      });
    }

    update() {
      const query = this.searchInput.value.trim().toLocaleLowerCase();
      let visibleCount = 0;

      this.items.forEach((item) => {
        const matchesSearch = !query || item.dataset.brandName.includes(query);
        const matchesLetter = this.activeLetter === 'all' || item.dataset.brandLetter === this.activeLetter;
        const isVisible = matchesSearch && matchesLetter;
        item.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      this.groups.forEach((group) => {
        group.hidden = !group.querySelector('[data-brand-item]:not([hidden])');
      });

      if (this.clearButton) this.clearButton.hidden = !query;
      if (this.emptyState) this.emptyState.hidden = visibleCount > 0;
      if (this.status) {
        const noun = visibleCount === 1 ? 'brand' : 'brands';
        this.status.textContent = `${visibleCount} ${noun}`;
      }
    }
  }

  customElements.define('brands-directory', BrandsDirectory);
}
